import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useLanguage } from "../../../Language";
import { Controller, useForm } from "react-hook-form";
import { axios } from "../../../AxiosInstanceManager";
import { AccountHolder } from "../../../Account";
import { TitleAndPubDate } from "../TitleAndPubDate";
import {
  AvailabilitySource,
  AvailabilitySourceService,
} from "../../../../contexts/EResourceAvailabilityContext";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { EResourceFormat } from "@tlc/ls2pac/dto/EResourceFormat";

interface EResourceCheckout {
  service: string;
  titleId: string;
  patronId: string;
  format: string;
}

export interface EResourceCheckoutResponse {
  service: AvailabilitySourceService; // string
  itemId: string;
  success: boolean;
  holdsPosition: number;
  itemNotAvailable: boolean;
  checkedOut: boolean;
  accountLocked: boolean;
  url: string;
  expirationDate: string; // Date;
  maxedOut: boolean;
  autoSync: boolean;
  format: string;
  actionEnabled: boolean;
  useRedirectForUrl: boolean;
  invalidUser: boolean;
  formatServiceToken: string;
  onHoldForAnotherUser: boolean;
  renewalsNotAllowed: boolean;
}

interface DownloadableCheckoutFormInput {
  format: string;
}

interface DownloadableCheckoutDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: EResourceCheckoutResponse) => void;
  resource: ResourceView | ResourceDWR;
  source: AvailabilitySource;
}
export const DownloadableCheckoutDialog = ({
  open,
  onClose,
  onSuccess,
  resource,
  source,
}: DownloadableCheckoutDialogProps) => {
  const { translate } = useLanguage();
  const formMethods = useForm<DownloadableCheckoutFormInput>({
    defaultValues: {},
  });
  const { handleSubmit, setValue } = formMethods;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const getFormatName = ({ formatToken, formatId }: EResourceFormat) => {
    if (formatToken && formatToken !== "<InvalidEResourceFormat>") {
      const languageToken = formatToken.replace(/</g, "").replace(/>/g, "");
      return translate(languageToken);
    }
    return formatId;
  };

  const getBakerAndTaylorForm = () => {
    return (
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">
          {translate("eResourceCheckoutFormatLabel")}
        </FormLabel>
        <Controller<DownloadableCheckoutFormInput>
          name="format"
          control={formMethods.control}
          render={({ field }) => (
            <RadioGroup {...field}>
              {source.formatInfo.map((fi) => (
                <FormControlLabel
                  key={fi.formatId}
                  value={fi.formatId}
                  control={<Radio />}
                  label={getFormatName(fi)}
                />
              ))}
            </RadioGroup>
          )}
        />
      </FormControl>
    );
  };

  const getDialogContent = () => {
    if (source.service === AvailabilitySourceService.bakertaylor) {
      setValue("format", source.formatInfo[0].formatId);
      if (source.formatInfo?.length > 1) {
        return getBakerAndTaylorForm();
      }
    }

    return (
      <List>
        <ListSubheader>{translate("thisTitleIsAvailableAs")}</ListSubheader>
        {source.formatInfo.map((fi) => (
          <ListItem>
            <ListItemText primary={getFormatName(fi)} />
          </ListItem>
        ))}
      </List>
    );
  };

  const handleError = (data: EResourceCheckoutResponse): string => {
    if (data.accountLocked) {
      return translate("eResourceAccountLockedMessage");
    }
    if (data.invalidUser) {
      return translate("eResourceInvalidUserMessage");
    }
    if (data.maxedOut) {
      return translate("PatronCannotCheckoutTitleToken");
    }
    if (data.onHoldForAnotherUser) {
      return translate("eResourceLoanIsOnHold");
    }
    if (data.checkedOut) {
      return translate("eResourceAlreadyCheckedOutErrorMessage");
    }
    if (data.itemNotAvailable) {
      return translate("eResourceItemNotAvailable");
    }
    if (data.renewalsNotAllowed) {
      return translate("eResourceLoanRenewalNotPermitted");
    }
    return translate("eResourceActionFailure");
  };

  const onSubmit = async ({ format }: DownloadableCheckoutFormInput) => {
    const payload: Omit<EResourceCheckout, "patronId"> = {
      format,
      service: source.service,
      titleId: source.itemId,
    };
    try {
      setLoading(true);
      const { data } = await axios.post<EResourceCheckoutResponse>(
        `eResource/checkout`,
        payload
      );
      if (data.success) {
        setError(null);
        // update counts
        AccountHolder.fetchAccountSummaryAndPatron(true);
        onSuccess(data);
      } else {
        setError(() => handleError(data));
      }
    } catch (e: unknown) {
      console.error(e);
      setError(translate("eResourceActionFailure"));
    } finally {
      setLoading(false);
    }
  };

  if (source?.availability) {
    return (
      <Dialog open={open} onClose={onClose}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{translate("checkoutEResourceDialogTitle")}</DialogTitle>
          <DialogContent>
            <Stack gap={2}>
              <TitleAndPubDate resource={resource} />
              {getDialogContent()}
              {!!error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{translate("cancel")}</Button>
            <Button
              disabled={!!error}
              loading={loading}
              type="submit"
              variant="contained"
            >
              {translate("ok")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  return null;
};
